// WhatsApp DIRECT SEND in the campaign composer — a written utility message instead of a
// pre-approved template, and the "unfinished checkouts" audience it is most often for.
//
// Everything the operator sees here is read from the backend, never re-derived: the audience
// counts, who was left out and why, the outcome vocabulary and its default, the sample person the
// preview is rendered for, the template name. The rules themselves live in tkt-backend
// (src/queries/unfinishedCheckouts.ts, src/services/directSend.ts); this file only shows them.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, ExternalLink, GitBranch, Loader2, Send } from 'lucide-react';
import { loadDirectSendMessages, loadDirectSendPerson, sendDirectSendTest, sendDirectSendToOne } from '../../lib/backOfficeApi';
import { Select } from '../../ui/Form';

// What happened to a checkout, in words an operator uses. Keys are the backend's.
const OUTCOME_LABEL = {
  no_attempt: 'Never reached payment',
  pending: 'Payment prompt never resolved',
  'failed:provider_error': 'Payment failed — gateway fault',
  'failed:config_error': 'Payment failed — our setup',
  'failed:no_response': 'Payment failed — prompt unanswered',
  'failed:unknown': 'Payment failed — no reason given',
  'failed:insufficient_funds': 'Insufficient funds',
  'failed:wrong_pin': 'Wrong PIN',
  'failed:cancelled_by_user': 'Cancelled the prompt',
  'failed:limit_exceeded': 'Over their limit',
  'failed:account_problem': 'Account problem',
  'failed:not_enrolled': 'Not a mobile-money user',
  'failed:invalid_phone': 'Invalid phone number',
  'failed:busy': 'Another transaction in progress',
};
const outcomeLabel = (k) => OUTCOME_LABEL[k] || k.replace(/^failed:/, 'Failed — ').replace(/_/g, ' ');

// The placeholders the backend accepts (DIRECT_SEND_VARIABLES). Anything else is refused there.
const VARIABLES = [
  { k: 'firstName', l: 'first name' },
  { k: 'eventName', l: 'event' },
  { k: 'orderRef', l: 'order ref' },
  { k: 'amount', l: 'amount' },
  { k: 'link', l: 'event link' },
];
const BODY_LIMIT = 1024;
const BUTTON_LIMIT = 20;

// datetime-local works in the browser's own zone; the backend receives instants.
const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const defaultUnfinishedWindow = () => {
  const to = new Date(Date.now() + 60_000);
  return { from: toLocalInput(new Date(to.getTime() - 7 * 86_400_000)), to: toLocalInput(to) };
};
// A half-typed date is not an instant: send nothing, and the backend's refusal names the problem.
export const windowToInstants = (w) => {
  const from = new Date(w.from), to = new Date(w.to);
  return isNaN(from) || isNaN(to) ? {} : { from: from.toISOString(), to: to.toISOString() };
};

/** Fill placeholders from the sample person — the same substitution the backend performs. */
export const renderWithSample = (body, sample) =>
  String(body || '').replace(/\{([a-zA-Z_]+)\}/g, (whole, k) => (sample && sample[k] ? String(sample[k]) : whole));

/* ── AUDIENCE: unfinished checkouts ────────────────────────────────────────────────────────── */

export const UnfinishedCheckoutsPicker = ({ windowValue, onWindowChange, outcomes, onOutcomesChange, detail, loading }) => {
  const counts = detail?.outcomes || {};
  const ours = new Set(detail?.notTheBuyersDoing || []);
  const present = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const theirsPresent = present.filter((k) => !ours.has(k));
  const oursPresent = present.filter((k) => ours.has(k));
  const selected = outcomes ? new Set(outcomes) : ours; // until touched: the backend's default
  const toggle = (k) => {
    const next = new Set(selected);
    next.has(k) ? next.delete(k) : next.add(k);
    onOutcomesChange([...next]);
  };
  const preset = (days) => {
    const to = new Date(Date.now() + 60_000);
    onWindowChange({ from: toLocalInput(new Date(to.getTime() - days * 86_400_000)), to: toLocalInput(to) });
  };
  const ex = detail?.excluded;
  const Chip = ({ k }) => (
    <label className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-[11.5px] ${selected.has(k) ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
      <span className="flex items-center gap-2 min-w-0"><input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} /><span className="text-[#1F2937] truncate">{outcomeLabel(k)}</span></span>
      <span className="font-mono tabular-nums text-[#6B7280]">{counts[k]}</span>
    </label>
  );

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280]">When they started checking out</div>
          <div className="flex gap-1">
            {[[1, '24 h'], [7, '7 days'], [30, '30 days']].map(([d, l]) => (
              <button key={d} onClick={() => preset(d)} className="h-6 px-2 text-[10.5px] rounded border border-[#E6E4E1] text-[#4B5563] hover:bg-[#F2F1ED]">Last {l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="datetime-local" value={windowValue.from} onChange={(e) => onWindowChange({ ...windowValue, from: e.target.value })} className="h-9 px-3 text-[12.5px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
          <input type="datetime-local" value={windowValue.to} onChange={(e) => onWindowChange({ ...windowValue, to: e.target.value })} className="h-9 px-3 text-[12.5px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
        </div>
        <div className="text-[10.5px] text-[#6B7280] mt-1">Telling people <em>we</em> had a problem? Set this to when the problem was live — it is what makes the message true for each person.</div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">What happened to their checkout</div>
        {loading && !detail ? (
          <div className="text-[11.5px] text-[#6B7280] inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Reading checkouts…</div>
        ) : present.length === 0 ? (
          <div className="text-[11.5px] text-[#9CA3AF] bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2.5">
            {detail?.event?.ended ? 'This event has ended — there is nothing left to buy.' : 'No unfinished checkouts in this window.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div className="space-y-1">
              <div className="text-[10px] text-[#6B7280]">Not their doing</div>
              {oursPresent.length ? oursPresent.map((k) => <Chip key={k} k={k} />) : <div className="text-[10.5px] text-[#9CA3AF]">None</div>}
            </div>
            <div className="space-y-1">
              <div className="text-[10px] text-[#6B7280]">Their own — wallet, PIN, cancelled</div>
              {theirsPresent.length ? theirsPresent.map((k) => <Chip key={k} k={k} />) : <div className="text-[10.5px] text-[#9CA3AF]">None</div>}
            </div>
          </div>
        )}
      </div>

      {ex && (
        <div className="text-[10.5px] text-[#6B7280] leading-relaxed">
          <span className="font-semibold text-[#4B5563]">Left out automatically:</span>{' '}
          {[
            [ex.boughtSince, 'already bought tickets for this event'],
            [ex.paidNotIssued, 'paid but no ticket was issued — a support case, not a reminder'],
            [ex.alreadySent, 'already received this exact message'],
            [ex.noPhone, 'no phone number'],
          ].filter(([n]) => n > 0).map(([n, t]) => `${n} ${t}`).join(' · ') || 'nobody'}
          . One message per person, however many times they tried.
        </div>
      )}
    </div>
  );
};

/* ── PREVIEW: the bubble a real person in the audience will see ────────────────────────────── */

export const WhatsAppBubble = ({ text, buttonText, link, caption }) => (
  <div className="bg-[#ECE5DD] rounded-md p-3">
    <div className="bg-white rounded-lg rounded-tl-none shadow-sm max-w-[320px] overflow-hidden">
      <div className="px-3 pt-2 pb-1.5 text-[12.5px] leading-[1.45] text-[#111B21] whitespace-pre-wrap break-words">
        {text || <span className="text-[#9CA3AF]">Your message…</span>}
      </div>
      {buttonText && (
        <a href={link || undefined} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 border-t border-[#E9EDEF] py-2 text-[12.5px] font-medium text-[#027EB5]">
          <ExternalLink className="w-3.5 h-3.5" />{buttonText}
        </a>
      )}
    </div>
    {caption && <div className="text-[10px] text-[#6B7280] mt-2">{caption}</div>}
  </div>
);

/* ── COMPOSE: the written message ─────────────────────────────────────────────────────────── */

export const DirectSendComposer = ({ value, onChange, detail, toOne = false }) => {
  const [saved, setSaved] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    const ac = new AbortController();
    loadDirectSendMessages({ signal: ac.signal }).then((d) => { if (!ac.signal.aborted) setSaved(d); }).catch(() => {});
    return () => ac.abort();
  }, []);

  const base = saved.find((m) => m.id === value.startedFrom);
  const changed = base && (base.body !== value.body || (base.buttonText || '') !== (value.buttonText || ''));
  const set = (patch) => onChange({ ...value, ...patch });

  const startFrom = (id) => {
    const m = saved.find((x) => x.id === id);
    if (!m) return set({ startedFrom: null });
    set({ startedFrom: m.id, body: m.body, buttonText: m.buttonText || '', withButton: !!m.buttonText, save: true, label: m.name });
  };
  const insert = (k) => {
    const el = ref.current;
    const token = `{${k}}`;
    const at = el ? el.selectionStart : value.body.length;
    const end = el ? el.selectionEnd : at;
    const body = value.body.slice(0, at) + token + value.body.slice(end);
    set({ body });
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + token.length; } });
  };

  const sample = detail?.sample;
  const total = detail?.count ?? 0;
  const alreadySent = detail?.excluded?.alreadySent || 0;
  const used = useMemo(() => new Set([...String(value.body).matchAll(/\{([a-zA-Z_]+)\}/g)].map((m) => m[1])), [value.body]);
  const missing = Object.entries(detail?.missingValues || {}).filter(([k, n]) => used.has(k) && n > 0);

  // WHO THESE WORDS REACH. The same words never go to the same person twice (the backend's rule),
  // so the audience can shrink — or empty — the moment a saved message is picked. Without this the
  // only sign was a disabled button and a preview that said "pick an audience" to someone who had.
  const reach = !detail || toOne ? null
    : total === 0 && alreadySent > 0
      ? { tone: 'warn', text: <>Everyone in this audience has already received these exact words, so nobody would get them again. Change the wording to send a new version — the saved original stays as it is.</> }
      : { tone: 'info', text: <>Reaches <b>{total}</b> {total === 1 ? 'person' : 'people'}{alreadySent > 0 ? <> · {alreadySent} already received these exact words and won’t get them again</> : null}.</> };

  return (
    <div className="grid grid-cols-[1fr_300px] gap-4">
      <div className="space-y-3">
        {saved.length > 0 && (
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1">Start from</div>
            <Select value={value.startedFrom || ''} onChange={(e) => startFrom(e.target.value)} className="w-full">
              <option value="">— A new message —</option>
              {saved.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.name}</option>)}
            </Select>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280]">Message</div>
            <div className={`text-[10.5px] tabular-nums ${value.body.length > BODY_LIMIT ? 'text-[#9B1F1F]' : 'text-[#9CA3AF]'}`}>{value.body.length} / {BODY_LIMIT}</div>
          </div>
          <textarea
            ref={ref}
            rows={7}
            value={value.body}
            onChange={(e) => set({ body: e.target.value })}
            placeholder={'Hi {firstName}, your payment for {eventName} (order {orderRef}) didn’t go through because of a problem on our side. It’s fixed now — you can get your tickets below.'}
            className="w-full px-3 py-2 text-[12.5px] leading-[1.5] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937] resize-y"
          />
          <div className="flex flex-wrap items-center gap-1 mt-1.5">
            <span className="text-[10.5px] text-[#6B7280] mr-0.5">Insert</span>
            {VARIABLES.map((v) => (
              <button key={v.k} onClick={() => insert(v.k)} className="h-6 px-2 text-[10.5px] rounded border border-[#E6E4E1] text-[#4B5563] hover:bg-[#F2F1ED] font-mono">{`{${v.k}}`}</button>
            ))}
          </div>
          {reach && (
            <div className={`mt-1.5 text-[10.5px] rounded px-2 py-1 border ${reach.tone === 'warn' ? 'text-[#8A5A12] bg-[#FEF7E6] border-[#FDE68A]' : 'text-[#4B5563] bg-[#FBFAF7] border-[#E6E4E1]'}`}>{reach.text}</div>
          )}
          {missing.map(([k, n]) => (
            <div key={k} className="mt-1.5 text-[10.5px] text-[#8A5A12] bg-[#FEF7E6] border border-[#FDE68A] rounded px-2 py-1">
              <span className="font-mono">{`{${k}}`}</span>: {n} of {total} {total === 1 ? 'person has' : 'people have'} no value — they won’t receive this message. Remove it to reach everyone.
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[12px] text-[#1F2937]"><input type="checkbox" checked={!!value.withButton} onChange={(e) => set({ withButton: e.target.checked })} />Add a button</label>
          {value.withButton && (
            <div className="flex-1 flex items-center gap-2">
              <input value={value.buttonText} maxLength={BUTTON_LIMIT} onChange={(e) => set({ buttonText: e.target.value })} placeholder="Get tickets" className="w-40 h-8 px-2.5 text-[12px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
              <span className="text-[10.5px] text-[#6B7280]">opens the event page</span>
            </div>
          )}
        </div>

        <div className="border-t border-[#EEEDEA] pt-3 space-y-1.5">
          {toOne
            ? <div className="text-[12px] text-[#1F2937]">Kept to send again — its name</div>
            : <label className="flex items-center gap-2 text-[12px] text-[#1F2937]"><input type="checkbox" checked={!!value.save} onChange={(e) => set({ save: e.target.checked })} />Save this message to reuse</label>}
          {value.save && (
            <input value={value.label} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Checkout issue fixed" className="w-full h-8 px-2.5 text-[12px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
          )}
          {value.save && base && (
            <div className="text-[10.5px] text-[#6B7280] inline-flex items-start gap-1.5">
              <GitBranch className="w-3.5 h-3.5 mt-px shrink-0" />
              {changed
                ? <span>The words differ from <b>{base.code}</b>, so this is saved as a new version of it. {base.code} stays exactly as it is — WhatsApp never edits a message it has already sent.</span>
                : <span>Same words as <b>{base.code}</b> — it is reused as it is, not copied.</span>}
            </div>
          )}
        </div>

        <div className="text-[10.5px] text-[#6B7280] leading-relaxed bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
          Sent without a pre-approved template (WhatsApp <b>Direct Send</b>, utility). Keep it about the person’s own order —
          no offers, discounts or “don’t miss out”. WhatsApp treats those as marketing, and repeated marketing through this
          route restricts it for the whole account.
        </div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1">Preview</div>
        <WhatsAppBubble
          text={renderWithSample(value.body, sample)}
          buttonText={value.withButton ? (value.buttonText || 'Get tickets') : null}
          link={sample?.link}
          caption={sample && toOne
            ? <>As they will see it — filled from order <b>{sample.orderRef}</b>.</>
            : sample
            ? <>As <b>{sample.firstName || 'this person'}</b> ({sample.phone}) will see it — a real person in this audience.</>
            : detail ? 'Nobody in this audience would receive these words, so there is no one to preview with.'
              : 'Pick an audience to preview with a real person’s details.'}
        />
      </div>
    </div>
  );
};

/* ── REVIEW: send one to your own phone first ─────────────────────────────────────────────── */

export const DirectSendTest = ({ eventId, content, sample, save, label }) => {
  const [to, setTo] = useState('');
  const [state, setState] = useState({ status: 'idle' });
  const run = async () => {
    setState({ status: 'sending' });
    try {
      const r = await sendDirectSendTest({ to, eventId, content, sample, save, label });
      setState({ status: 'sent', to: r.to, templateName: r.templateName });
    } catch (e) {
      setState({ status: 'error', message: e.message });
    }
  };
  return (
    <div className="bg-white border border-[#E6E4E1] rounded-md p-3 space-y-2">
      <div className="text-[12px] font-semibold text-[#1F2937]">Send a test to your own WhatsApp first</div>
      <div className="text-[10.5px] text-[#6B7280]">It goes through exactly the path the campaign will, filled with the preview person’s details. It is also how you find out whether Direct Send is enabled for this WhatsApp account — before anyone else gets anything.</div>
      <div className="flex gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Your number, e.g. 0772 123 456" className="flex-1 h-8 px-2.5 text-[12px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
        <button onClick={run} disabled={!to.trim() || state.status === 'sending'} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5">
          {state.status === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Send test
        </button>
      </div>
      {state.status === 'sent' && (
        <div className="text-[11px] text-[#166534] inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />Sent to {state.to}{state.templateName ? <> · WhatsApp template <span className="font-mono">{state.templateName}</span></> : null}</div>
      )}
      {state.status === 'error' && <div className="text-[11px] text-[#9B1F1F]">{state.message}</div>}
    </div>
  );
};

/* ── ONE PERSON, FROM THEIR CHAT ──────────────────────────────────────────────────────────────── */

/**
 * A saved wording sent to the person in this chat, about one of their own orders, after WhatsApp's 24-hour
 * window has closed. The words are the template (same words = the same Meta template), so they are always kept,
 * and every {placeholder} is filled from the order picked here — never typed — which is what lets the same message
 * go to the next person it is true for.
 */
export const WriteToOne = ({ to, name, onSent }) => {
  const [orders, setOrders] = useState({ status: 'loading', rows: [] });
  const [orderId, setOrderId] = useState('');
  const [value, setValue] = useState({ body: '', buttonText: '', withButton: false, save: true, label: '', startedFrom: null });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    const ac = new AbortController();
    loadDirectSendPerson(to, { signal: ac.signal })
      .then((rows) => { if (ac.signal.aborted) return; setOrders({ status: 'ready', rows }); if (rows[0]) setOrderId(rows[0].id); })
      .catch((e) => { if (!ac.signal.aborted) setOrders({ status: 'error', rows: [], error: e.message }); });
    return () => ac.abort();
  }, [to]);
  const order = orders.rows.find((o) => o.id === orderId);
  const sample = order ? { firstName: order.firstName, eventName: order.eventName, orderRef: order.orderRef, amount: order.amount, phone: to } : null;
  const used = new Set([...String(value.body).matchAll(/\{([a-zA-Z_]+)\}/g)].map((m) => m[1]));
  const missingValues = Object.fromEntries([...used].filter((k) => sample && k !== 'link' && !sample[k]).map((k) => [k, 1]));
  const ready = order && value.body.trim() && (value.label.trim() || value.startedFrom) && !Object.keys(missingValues).length;

  const send = async () => {
    if (!ready || sending) return;
    if (!window.confirm(`Send this WhatsApp message to ${name || to}?`)) return;
    setSending(true); setError(null);
    try {
      const r = await sendDirectSendToOne({ to, orderId, content: { body: value.body, buttonText: value.withButton ? value.buttonText : null }, label: value.label, startedFrom: value.startedFrom });
      onSent?.(r);
    } catch (e) { setError(e.message); } finally { setSending(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1">About their order</div>
        {orders.status === 'loading' && <div className="text-[11.5px] text-[#9CA3AF] inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Their orders…</div>}
        {orders.status === 'error' && <div className="text-[11.5px] text-[#9B1F1F]">{orders.error}</div>}
        {orders.status === 'ready' && !orders.rows.length && <div className="text-[11.5px] text-[#6B7280]">No orders on this number — a Direct Send is about the person's own order, so there is nothing to write about.</div>}
        {orders.rows.length > 0 && (
          <Select value={orderId} onChange={(e) => setOrderId(e.target.value)} className="w-full">
            {orders.rows.map((o) => <option key={o.id} value={o.id}>{o.orderRef} · {o.eventName || 'No event'} · {o.firstName || '—'} · {o.amount || 'no amount'} · {o.status}</option>)}
          </Select>
        )}
      </div>
      <DirectSendComposer value={value} onChange={setValue} detail={sample ? { sample, count: 1, missingValues } : null} toOne />
      {error && <div className="text-[11.5px] text-[#9B1F1F]">{error}</div>}
      <div className="flex justify-end">
        <button onClick={send} disabled={!ready || sending} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Send to {name || to}
        </button>
      </div>
    </div>
  );
};
